import {
  ExportFormat,
  ExportMode,
  ExportOption,
  ExportType,
} from '@/modules/exports'
import { endOfWeek, format } from '@/utils/date-fns'
import { notEmptyArrayValidation } from '@/utils/forms'
import { useCompany, useConfirm, useOnErrorAuto, useUser } from '@/utils/hooks'
import { isInvalidDate } from '@/utils/time'
import { useQuery } from '@apollo/client'
import type { SelectOption } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  addHours,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  endOfDay,
  endOfMonth,
  formatISO,
  getDaysInMonth,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns'
import { addDays } from 'date-fns/esm'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { useCallback, useMemo } from 'react'
import { Time } from '../../lib/time'
import { getLocaleCorrectionOffset } from '../../utils/date/localeCorrection'
import { Module } from '../companies'
import type {
  ActivitiesWithReceiptCoolumnNode,
  AllDynamicColumnsQuery,
  AllDynamicColumnsQueryVariables,
} from '../dynamic_tables'
import { ALL_DYNAMIC_COLUMNS_QUERY } from '../dynamic_tables'
import type { UserNode } from '../users'
import type {
  Activity,
  Infringement,
  Issue,
} from './components/detail/Schedule.types'
import { DriverActivityType } from './consts'
import {
  useActivityExportMutations,
  useActivityMutations,
} from './mutations.hooks'
import {
  DRIVER_ACTIVITY_DAY_REPORT_QUERY,
  GET_ALL_DRIVER_ACTIVITIES,
  GET_DRIVER_ACTIVITIES_AGGREGATED,
  GET_ISSUES,
  GET_SPECIFIC_CONDITIONS,
} from './queries'
import type {
  ActivitiesForm,
  GeneratePDFParams,
  RangeNode,
  RangeNodeCollection,
  ReportDataItem,
  TypeChart,
  UsePDFGeneratorParams,
} from './types'
import { UserOrderBy } from './types'
import type {
  DriverActivityDayReportQuery,
  DriverActivityDayReportQueryReportData,
  DriverActivityDayReportQueryVariables,
  GetAllDriverActivitiesAggregated,
  GetIssues,
  GetSpecificConditionsData,
  ParamChart,
  SpecificConditionNode,
} from './types.graphql'
import {
  enforceHourMinuteString,
  getNodeTime,
  optimizeVehicleNodes,
  parseNumberToTimestamp,
} from './util'
import { ALL_INFRINGEMENTS_QUERY } from '../infringements'

export function useActivityApprovedHandlers() {
  const translations = useTranslate({
    prompt: {
      title: 'activities.approve-title',
      day: ['activities.approve-day', { date: '' }],
      week: [
        'activities.approve-week',
        { week: '', dateStart: '', dateEnd: '' },
      ],
      month: [
        'activities.approve-month',
        { month: '', dateStart: '', dateEnd: '' },
      ],
    },
  })

  const confirm = useConfirm()
  const mutations = useActivityMutations()

  async function handleApproveDay(
    userId: string,
    approve: boolean,
    datetimeStart: Date
  ) {
    const { data } = await confirm(
      translations.prompt.day({ date: format(datetimeStart, 'dd.MM') }),
      translations.prompt.title
    )
    if (!data) return
    const datetimeEnd = endOfDay(datetimeStart)
    void mutations.toggleApproved({
      variables: {
        user: userId,
        datetimeStart: datetimeStart.toISOString(),
        datetimeEnd: datetimeEnd.toISOString(),
        approve,
      },
    })
  }

  async function handleApproveWeek(
    userId: string,
    approve: boolean,
    datetimeStart: Date,
    datetimeEnd: Date
  ) {
    const { data } = await confirm(
      translations.prompt.week({
        week: format(datetimeStart, 'w'),
        dateStart: format(datetimeStart, 'dd.MM'),
        dateEnd: format(datetimeEnd, 'dd.MM'),
      }),
      translations.prompt.title
    )
    if (!data) return

    void mutations.toggleApproved({
      variables: {
        user: userId,
        datetimeStart: datetimeStart.toISOString(),
        datetimeEnd: datetimeEnd.toISOString(),
        approve,
      },
    })
  }

  async function handleApproveMonth(
    userId: string,
    approve: boolean,
    datetimeStart: Date,
    datetimeEnd: Date
  ) {
    const { data } = await confirm(
      translations.prompt.month({
        month: format(endOfWeek(datetimeStart), 'MMMM'),
        dateStart: format(datetimeStart, 'dd.MM'),
        dateEnd: format(datetimeEnd, 'dd.MM'),
      }),
      translations.prompt.title
    )
    if (!data) return

    void mutations.toggleApproved({
      variables: {
        user: userId,
        datetimeStart: datetimeStart.toISOString(),
        datetimeEnd: datetimeEnd.toISOString(),
        approve,
      },
    })
  }

  return {
    handler: {
      day: handleApproveDay,
      week: handleApproveWeek,
      month: handleApproveMonth,
    },
  }
}

export function useActivityExportForm(onCompleted?: () => void) {
  const translations = useTranslate({
    hours: 'common.hours',
    diet: 'common.diet',
    tours: 'common.tours',

    error: 'error.generic-server-error',
    mailSuccess: 'exports.success-email-process',
    selectUsersError: 'activities.errors.select-multiple-users',
  })

  const me = useUser()
  const company = useCompany()

  const { exportActivities, loading: mailLoading } =
    useActivityExportMutations(onCompleted)

  const exportFormat = useMemo<SelectOption<ExportType>[]>(
    () => [
      {
        value: ExportType.ACTIVITIES,
        label: translations.hours,
      },
      {
        value: ExportType.DIET,
        label: translations.diet,
      },
      {
        value: ExportType.TOURS,
        label: translations.tours,
      },
    ],
    [translations.diet, translations.hours, translations.tours]
  )

  const {
    formValues: form,
    formErrors: errors,
    submitHandler: submit,
    updateForm,
  } = useForm<ActivitiesForm>({
    values: {
      month: new Date(),
      dateStart: new Date(),
      dateEnd: new Date(),
      exportType: ExportType.ACTIVITIES,
      exportOption: ExportOption.DOWNLOAD,
      exportFormat: ExportFormat.PDF,
      orderBy: UserOrderBy.FAMILY_NAME,
      useDateRange: false,
      weekly: false,
      user: null,
      users: [],
    },
    validators: {
      users: value =>
        notEmptyArrayValidation(value) ? translations.selectUsersError : null,
    },
  })

  const handler = useCallback(
    <K extends keyof ActivitiesForm>(key: K) => {
      return (value: ActivitiesForm[K] | null) => {
        if (value === null) return
        updateForm({ [key]: value })
      }
    },
    [updateForm]
  )

  const getDateInterval = useCallback(
    (startDate: Date, endDate?: Date): [string, string] => {
      if (form.useDateRange) {
        const dateEnd = endDate || new Date()
        return [formatISO(startDate), formatISO(dateEnd)]
      }
      return [
        formatISO(startOfMonth(startDate)),
        formatISO(endOfMonth(startDate)),
      ]
    },
    [form.useDateRange]
  )

  const handleExport = useCallback(
    async (values: ActivitiesForm) => {
      if (!values.user) return
      const date = form.useDateRange
        ? form.dateStart
        : startOfMonth(values.month)
      const [dateStart, dateEnd] = getDateInterval(date, form.dateEnd)

      void exportActivities({
        variables: {
          exportType: values.exportType,
          exportFormat: values.exportFormat,
          userMail: me.id,
          company: company.id,
          dateStart: dateStart,
          dateEnd: dateEnd,
          user: values.user,
          exportOption: values.exportOption,
          weekly: values.weekly,
          exportMode: ExportMode.DRIVER,
        },
      })
    },
    [
      company.id,
      exportActivities,
      form.dateEnd,
      form.dateStart,
      form.useDateRange,
      getDateInterval,
      me.id,
    ]
  )

  return {
    form,
    errors,
    loading: mailLoading,
    mailLoading,
    exportFormat,

    handler,
    updateForm,
    submit,
    handleExport,
  }
}

export function useMassActivityExportForm(onCompleted?: () => void) {
  const translations = useTranslate({
    hours: 'common.hours',
    hoursWorkDay: 'common.hours-work-day',
    diet: 'common.diet',
    tours: 'common.tours',
    summary: 'activities.work-summary',
    overtime: 'common.overtime',

    error: 'error.generic-server-error',
    mailSuccess: 'exports.success-email-process',

    selectUsersError: 'activities.errors.select-multiple-users',
    pauseExport: 'activities.pause-export',
    pauseAndAvailabilityExport: 'activities.pause-and-availability-export',
    workDayExport: 'activities.work-day-report',
    overtimeAndNightWorkExport: 'activities.overtime-and-night-work',
  })

  const me = useUser()
  const company = useCompany()

  const { exportActivitiesMass, loading: mailLoading } =
    useActivityExportMutations(onCompleted)

  const exportTypes = useMemo<SelectOption<ExportType>[]>(
    () => [
      {
        value: ExportType.MASS_ACTIVITIES,
        label: translations.hours,
      },
      {
        value: ExportType.MASS_ACTIVITIES_WORK_DAY,
        label: translations.hoursWorkDay,
      },
      {
        value: ExportType.MASS_DIET,
        label: translations.diet,
      },
      {
        value: ExportType.MASS_TOURS,
        label: translations.tours,
      },
      {
        value: ExportType.MASS_SUMMARY,
        label: translations.summary,
      },
      {
        value: ExportType.MASS_ACTIVITIES_OVERTIME,
        label: translations.overtime,
      },
      {
        value: ExportType.MASS_PAUSE,
        label: translations.pauseExport,
      },
      {
        value: ExportType.MASS_PAUSE_WITH_AVAILABILITY,
        label: translations.pauseAndAvailabilityExport,
      },
      {
        value: ExportType.WORK_DAY_REPORT,
        label: translations.workDayExport,
      },
      {
        value: ExportType.OVERTIME_AND_NIGHT_WORK,
        label: translations.overtimeAndNightWorkExport,
      },
    ],
    [
      translations.diet,
      translations.hours,
      translations.hoursWorkDay,
      translations.overtime,
      translations.overtimeAndNightWorkExport,
      translations.pauseAndAvailabilityExport,
      translations.pauseExport,
      translations.summary,
      translations.tours,
      translations.workDayExport,
    ]
  )

  const excelExportTypes = useMemo(
    () => [
      ExportType.MASS_ACTIVITIES,
      ExportType.MASS_DIET,
      ExportType.WORK_DAY_REPORT,
      ExportType.OVERTIME_AND_NIGHT_WORK,
    ],
    []
  )

  const {
    formValues: form,
    formErrors: errors,
    submitHandler: submit,
    formValid,
    updateForm,
  } = useForm<ActivitiesForm>({
    values: {
      month: new Date(),
      dateStart: new Date(),
      dateEnd: new Date(),
      exportType: ExportType.MASS_ACTIVITIES,
      exportOption: ExportOption.DOWNLOAD,
      exportFormat: ExportFormat.PDF,
      useDateRange: false,
      user: null,
      users: [],
      orderBy: UserOrderBy.FAMILY_NAME,
      includeAbsences: false,
      weekly: false,
    },
    validators: {
      users: value =>
        notEmptyArrayValidation(value) ? translations.selectUsersError : null,
    },
    config: {
      initAsInvalid: true,
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'Export-activites-mass-form',
        transformValues: {
          month: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          exportType: value => value as ExportType,
          exportFormat: value => value as ExportFormat,
          exportOption: value => value as ExportOption,
          weekly: value => value as boolean,
        },
      },
    },
  })

  const handler = useCallback(
    <K extends keyof ActivitiesForm>(key: K) => {
      return (value: ActivitiesForm[K] | null) => {
        if (value === null) return
        updateForm({ [key]: value })
      }
    },
    [updateForm]
  )

  const getDateInterval = useCallback(
    (startDate: Date, endDate?: Date): [string, string] => {
      if (form.useDateRange) {
        const dateEnd = endDate || new Date()
        return [formatISO(startDate), formatISO(dateEnd)]
      }

      return [
        formatISO(startOfMonth(startDate)),
        formatISO(endOfMonth(startDate)),
      ]
    },
    [form.useDateRange]
  )

  const getExportDateInterval = useCallback(
    (): [string, string] =>
      getDateInterval(
        form.useDateRange ? form.dateStart : startOfMonth(form.month),
        form.dateEnd
      ),
    [
      form.dateEnd,
      form.dateStart,
      form.month,
      form.useDateRange,
      getDateInterval,
    ]
  )

  const handleExport = useCallback(
    async (values: ActivitiesForm) => {
      const date = form.useDateRange
        ? form.dateStart
        : startOfMonth(values.month)
      const [dateStart, dateEnd] = getDateInterval(date, form.dateEnd)
      const orderBy = values.orderBy ? values.orderBy : 'last_name'
      const includeAbsences = values.includeAbsences ?? false

      await exportActivitiesMass({
        variables: {
          exportType: values.exportType,
          exportFormat: values.exportFormat,
          user: me.id,
          users: form.users,
          company: company.id,
          dateStart: dateStart,
          dateEnd: dateEnd,
          orderBy: orderBy,
          includeAbsences: includeAbsences,
          exportOption: values.exportOption,
          weekly: values.weekly,
          leaderGroups: [],
        },
      })
    },
    [
      company.id,
      exportActivitiesMass,
      form.dateEnd,
      form.dateStart,
      form.useDateRange,
      form.users,
      getDateInterval,
      me.id,
    ]
  )

  return {
    form,
    errors,
    loading: mailLoading,
    exportTypes,
    excelExportTypes,
    formValid,
    handler,
    updateForm,
    submit,
    handleExport,
    getExportDateInterval,
  }
}

export function useGetActivitiesByType(
  activities: Activity[],
  report: DriverActivityDayReportQueryReportData | null,
  date: Date,
  issues: Issue[],
  infringements: Infringement[],
  specificConditions: SpecificConditionNode[],
  variant: TypeChart,
  infoTooltip?: string[],
  customDaysCount?: number,
  timeWorkTotal?: string | null
) {
  const translations = useTranslate({
    issues: 'common.issues',
    events: 'common.events',
    work: 'activities.work',
    rest: 'activities.rest',
    pause: 'common.pause',
    driving: 'activities.driving',
    countries: 'common.countries',
    vehicle: 'common.vehicle',
    available: 'common.availability',

    hours: ['common.hours', val => val.toLowerCase()],
    minutes: ['common.minutes', val => val.toLowerCase()],

    unavailable: 'common.unavailable',
    unknown: 'common.unknown',
  })

  const dstOffset = report?.daylightSavingsChange?.offset ?? 0
  const dstTimestamp = report?.daylightSavingsChange?.changeTime ?? 0
  const newUTCOffset = report?.daylightSavingsChange?.utcNewOffset ?? 0
  const timezoneData = report?.timezoneData
  const isMonthlyMode = variant === 'month'

  const periodStart = useMemo(() => {
    const startFunctions = {
      week: () => startOfWeek(date, { weekStartsOn: 1 }),
      month: () => startOfMonth(date),
      year: () => startOfYear(date),
      day: () => date,
    }
    return startFunctions[variant]?.() || date
  }, [date, variant])

  function getActivitySettings(activityType: string) {
    const settingsMap: Record<
      string,
      { color: string; height: string; label: string }
    > = {
      [DriverActivityType.WORK]: {
        color: 'orange',
        height: '100%',
        label: translations.work,
      },
      [DriverActivityType.AVAILABLE]: {
        color: 'secondary',
        height: '80%',
        label: translations.available,
      },
      [DriverActivityType.PAID_REST]: {
        color: 'blue400',
        height: '25%',
        label: translations.pause,
      },
      [DriverActivityType.DRIVE]: {
        color: 'green600',
        height: '100%',
        label: translations.driving,
      },
      Country: {
        color: 'tertiary',
        height: '10%',
        label: translations.countries,
      },
      [DriverActivityType.REST]: {
        color: 'gray7',
        height: '25%',
        label: translations.rest,
      },
    }

    return (
      settingsMap[activityType] || {
        color: 'red700',
        height: '10%',
        label: translations.unknown,
      }
    )
  }

  function calculateTimezoneOffsets(activityDate: Date) {
    if (!timezoneData)
      return { browserOffset: 0, nextDayOffset: 0, changeOffset: 0 }

    const browserOffset = getLocaleCorrectionOffset(timezoneData, activityDate)
    const nextDayOffset = getLocaleCorrectionOffset(
      timezoneData,
      addDays(activityDate, 1)
    )
    const changeOffset = nextDayOffset - browserOffset

    return { browserOffset, nextDayOffset, changeOffset }
  }

  function calculateActivityTimes(
    activity: Activity,
    referenceDate: Date,
    timezoneOffsets: ReturnType<typeof calculateTimezoneOffsets>
  ) {
    const { browserOffset, changeOffset } = timezoneOffsets

    const dateStart = addHours(new Date(activity.datetimeStart), browserOffset)
    const dateEnd = addHours(new Date(activity.datetimeEnd), browserOffset)
    const realDifferenceMs = dateEnd.getTime() - dateStart.getTime()

    const startYesterday =
      isBefore(dateStart, referenceDate) ||
      dateStart.getMinutes() + dateStart.getHours() === 0

    const nextDayRef = addDays(referenceDate, 1)
    const endNextDay =
      isAfter(dateEnd, nextDayRef) ||
      dateEnd.toDateString() === nextDayRef.toDateString()

    const [timeStart, timeStartTextCorrection] = startYesterday
      ? [new Time(0, 0), 0]
      : getNodeTime(
          dateStart,
          dstTimestamp,
          dstOffset,
          activity.datetimeStart,
          newUTCOffset,
          browserOffset,
          changeOffset
        )

    const [timeEnd, timeEndTextCorrection] = endNextDay
      ? [new Time(24 - dstOffset, 0), dstOffset]
      : getNodeTime(
          dateEnd,
          dstTimestamp,
          dstOffset,
          activity.datetimeEnd,
          newUTCOffset,
          browserOffset,
          changeOffset
        )

    const extendGraphEdges = (startYesterday ? 1 : 0) + (endNextDay ? 1 : 0)

    return {
      dateStart,
      dateEnd,
      realDifferenceMs,
      timeStart,
      timeEnd,
      timeStartTextCorrection,
      timeEndTextCorrection,
      extendGraphEdges,
      startYesterday,
      endNextDay,
    }
  }

  function createActivityNodes(
    activity: Activity,
    timeData: ReturnType<typeof calculateActivityTimes>,
    dayOffset: number = 0
  ): [RangeNode, RangeNode] {
    const {
      timeStart,
      timeEnd,
      timeStartTextCorrection,
      timeEndTextCorrection,
    } = timeData

    const difference = timeStart.difference(timeEnd, 'minute')
    const activitySettings = getActivitySettings(activity.activityType)

    return [
      {
        type: 'regular',
        time: timeStart.reduce('hours', false) + dayOffset,
        info: {
          label: activitySettings?.label,
          time: timeStart.add(timeStartTextCorrection, 'hours').format('HH:mm'),
          registrationNumbers: infoTooltip,
          date: new Date(activity.datetimeStart),
        },
        rangeText: formatDifference(difference),
        rangeSubText:
          difference >= 60 ? translations.hours : translations.minutes,
        background: activitySettings.color,
        height: activitySettings.height,
      },
      {
        type: 'regular',
        time: timeEnd.reduce('hours', false) + dayOffset,
        info: {
          label: activitySettings?.label,
          time: timeEnd.add(timeEndTextCorrection, 'hours').format('HH:mm'),
          registrationNumbers: infoTooltip,
          date: new Date(activity.datetimeStart),
        },
      },
    ]
  }

  function createVehicleNodes(
    activity: Activity,
    timeData: ReturnType<typeof calculateActivityTimes>,
    referenceDate: Date,
    dayOffset: number = 0
  ): [RangeNode, RangeNode] {
    const {
      timeStart,
      timeEnd,
      timeStartTextCorrection,
      timeEndTextCorrection,
      extendGraphEdges,
    } = timeData

    const identification = getVehicleIdentification(activity, translations)
    const activitySettings = getActivitySettings(activity.activityType)

    return [
      {
        type: 'regular',
        time: timeStart.reduce('hours', false) + dayOffset,
        info: {
          label: activitySettings?.label,
          time: timeStart.add(timeStartTextCorrection, 'hours').format('HH:mm'),
          registrationNumbers: infoTooltip,
          date: referenceDate,
        },
        rangeText: activity.vehicleRegistrationNumber,
        rangeSubText: identification,
        vehicleRange: true,
      },
      {
        type: 'regular',
        time: timeEnd.reduce('hours', false) + dayOffset + extendGraphEdges,
        info: {
          label: activitySettings?.label,
          time: timeEnd.add(timeEndTextCorrection, 'hours').format('HH:mm'),
          registrationNumbers: infoTooltip,
          date: new Date(activity.datetimeStart),
        },
      },
    ]
  }

  function getVehicleIdentification(activity: Activity, translations: any) {
    if (
      activity.activityType === DriverActivityType.NOT_AVAILABLE &&
      !activity.vehicleVin
    ) {
      return translations.unavailable
    }
    return activity.vehicleVin ?? translations.unknown
  }

  function formatDifference(difference: number): string {
    if (difference >= 60) {
      const hours = Math.floor(difference / 60)
      const minutes = difference % 60
      return `${hours}:${minutes.toString().padStart(2, '0')}`
    }
    return String(difference)
  }

  function categorizeActivityNodes(
    nodes: [RangeNode, RangeNode],
    activity: Activity,
    realDifferenceMs: number,
    acc: Omit<
      RangeNodeCollection,
      'issueNodes' | 'countryNodes' | 'infringementNodes'
    >
  ) {
    const activityTypeHandlers: Record<DriverActivityType, () => void> = {
      [DriverActivityType.NOT_AVAILABLE]: () => {},
      [DriverActivityType.AVAILABLE]: () => acc.availableNodes.push(nodes),
      [DriverActivityType.WORK]: () => acc.workNodes.push(nodes),
      [DriverActivityType.PAID_REST]: () => acc.pauseNodes.push(nodes),
      [DriverActivityType.DRIVE]: () => acc.driveNodes.push(nodes),
      [DriverActivityType.REST]: () => {
        const shouldBePause = shouldRestBePause(timeWorkTotal, realDifferenceMs)

        if (shouldBePause) {
          const activitySettings = getActivitySettings(
            DriverActivityType.PAID_REST
          )
          acc.pauseNodes.push([
            {
              ...nodes[0],
              background: activitySettings.color,
              info: { ...nodes[0].info, label: activitySettings?.label },
            },
            nodes[1],
          ])
        } else {
          acc.restNodes.push(nodes)
        }
      },
    }

    const handler =
      activityTypeHandlers[activity.activityType as DriverActivityType]
    handler?.()
  }

  function shouldRestBePause(
    timeWorkTotal: string | null | undefined,
    realDifferenceMs: number
  ): boolean {
    if (!timeWorkTotal) return false
    const quarterHour = 0.25 * 60 * 60 * 1000
    const sixHours = 6 * 60 * 60 * 1000

    return quarterHour <= realDifferenceMs && realDifferenceMs <= sixHours
  }

  function processTimeBasedNodes(
    items: (Issue | SpecificConditionNode | Infringement)[],
    type: 'issue' | 'specificCondition' | 'infringement',
    dayOffsetCalculator?: (
      item: Issue | SpecificConditionNode | Infringement
    ) => number
  ): RangeNode[][] {
    return items.reduce<RangeNode[][]>((acc, item) => {
      const timestamp =
        'timeOfIssueStart' in item
          ? item.timeOfIssueStart
          : 'infringementTimestamp' in item
          ? item.infringementTimestamp
          : item?.timestamp
      const dayOffset = dayOffsetCalculator?.(item) ?? 0

      if (dayOffset < 0) return acc

      const time = new Time(timestamp)
      const node: RangeNode = {
        type,
        time: time.reduce('hours', false) + dayOffset,
        text: time.format('HH:mm'),
        rangeSubText: 'type' in item ? item.type : undefined,
        icon:
          'type' in item
            ? item.type.includes('OUT')
              ? 'sign-out'
              : 'ferry'
            : undefined,
        background:
          type === 'issue' || type === 'infringement' ? 'red' : 'primary500',
        ...((type === 'issue' &&
          'id' in item && { href: `/issues/${item.id}` }) ||
          (type === 'infringement' &&
            'id' in item && { href: `/infringements/${item.id}` })),
      }

      acc.push([node])
      return acc
    }, [])
  }

  function optimizeAndColorizeVehicleNodes(
    vehicleNodes: [RangeNode, RangeNode][]
  ) {
    const optimized = optimizeVehicleNodes(vehicleNodes) as [
      RangeNode,
      RangeNode,
    ][]
    return optimized.map<[RangeNode, RangeNode]>(([startNode, endNode]) => [
      {
        ...startNode,
      },
      endNode,
    ])
  }

  const createWeekendNode = useCallback(
    (dayOffset: number, isFullDay = true): [RangeNode, RangeNode] => {
      const startTime = dayOffset
      const endTime = dayOffset + (isFullDay ? 24 : 1)

      return [
        {
          type: 'regular',
          time: startTime,
          info: {
            label: translations.rest,
            time: '00:00',
            registrationNumbers: infoTooltip,
            date: addDays(periodStart, Math.floor(dayOffset / 24)),
          },
          rangeText: isFullDay ? '23:00' : '00:00',
          rangeSubText: translations.hours,
          background: 'weekend',
          height: '100%',
        },
        {
          type: 'regular',
          time: endTime,
          info: {
            label: translations.rest,
            time: isFullDay ? '23:00' : '00:00',
            registrationNumbers: infoTooltip,
            date: addDays(periodStart, Math.floor(dayOffset / 24)),
          },
        },
      ]
    },
    [translations.rest, translations.hours, infoTooltip, periodStart]
  )

  function addWeekendNodesIfMissing(
    activityNodes: Omit<RangeNodeCollection, 'issueNodes' | 'countryNodes'>,
    startDate: Date,
    totalDays: number,
    isYearMode = false
  ) {
    for (let dayIndex = 0; dayIndex < totalDays; dayIndex++) {
      const currentDate = addDays(startDate, dayIndex)
      const dayOfWeek = currentDate.getDay()

      if (dayOfWeek === 0 || dayOfWeek === 6) {
        const dayOffset = isYearMode
          ? differenceInCalendarMonths(
              startOfMonth(currentDate),
              startOfMonth(startDate)
            ) * 24
          : dayIndex * 24
        const hasActivitiesOnDay = activities.some(activity => {
          const activityDate = new Date(activity.datetimeStart)
          const timezoneOffsets = calculateTimezoneOffsets(currentDate)
          const activityDateWithTZ = addHours(
            activityDate,
            timezoneOffsets.browserOffset
          )
          const activityEndWithTZ = addHours(
            new Date(activity.datetimeEnd),
            timezoneOffsets.browserOffset
          )

          return (
            isSameDay(activityDateWithTZ, currentDate) ||
            (isBefore(activityDateWithTZ, currentDate) &&
              isAfter(activityEndWithTZ, currentDate))
          )
        })

        if (!hasActivitiesOnDay) {
          const weekendNode = isYearMode
            ? createWeekendNode(dayOffset, false)
            : createWeekendNode(dayOffset, true)

          activityNodes.restNodes.push(weekendNode)
        }
      }
    }
  }

  function sortNodesByStart(nodes: RangeNode[][]) {
    return nodes.sort((a, b) => (a?.[0]?.time ?? 0) - (b?.[0]?.time ?? 0))
  }

  function dayOffsetCalculator(
    item: Issue | SpecificConditionNode | Infringement
  ) {
    const timestamp =
      'timeOfIssueStart' in item
        ? item.timeOfIssueStart
        : 'infringementTimestamp' in item
        ? item.infringementTimestamp
        : item?.timestamp
    const itemDate = new Date(timestamp)
    return isSameDay(itemDate, date) ? 0 : -1
  }

  const dayData: RangeNodeCollection = (() => {
    const activityNodes = activities.reduce<
      Omit<
        RangeNodeCollection,
        'issueNodes' | 'infringementNodes' | 'countryNodes'
      >
    >(
      (acc, activity) => {
        const timezoneOffsets = calculateTimezoneOffsets(date)
        const timeData = calculateActivityTimes(activity, date, timezoneOffsets)

        const nodes = createActivityNodes(activity, timeData)
        const vehicleNodes = createVehicleNodes(activity, timeData, date)

        acc.vehicleNodes.push(vehicleNodes)
        categorizeActivityNodes(nodes, activity, timeData.realDifferenceMs, acc)

        return acc
      },
      {
        workNodes: [],
        specificConditionNodes: [],
        restNodes: [],
        pauseNodes: [],
        availableNodes: [],
        driveNodes: [],
        vehicleNodes: [],
      }
    )

    const colorfulVehicleNodes = optimizeAndColorizeVehicleNodes(
      activityNodes.vehicleNodes
    )
    const issueNodes = processTimeBasedNodes(
      issues,
      'issue',
      dayOffsetCalculator
    )
    const infringementNodes = processTimeBasedNodes(
      infringements,
      'infringement',
      dayOffsetCalculator
    )
    const specificConditionNodes = processTimeBasedNodes(
      specificConditions,
      'specificCondition',
      dayOffsetCalculator
    )

    return {
      ...activityNodes,
      issueNodes,
      infringementNodes,
      specificConditionNodes,
      countryNodes: [],
      vehicleNodes: colorfulVehicleNodes,
    }
  })()

  const weekData: RangeNodeCollection = (() => {
    const result: Omit<RangeNodeCollection, 'issueNodes' | 'countryNodes'> = {
      workNodes: [],
      specificConditionNodes: [],
      infringementNodes: [],
      restNodes: [],
      pauseNodes: [],
      availableNodes: [],
      driveNodes: [],
      vehicleNodes: [],
    }

    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const currentDate = addDays(periodStart, dayIndex)
      const dayOffset = dayIndex * 24

      const dayActivities = activities.filter(activity => {
        const activityDate = new Date(activity.datetimeStart)
        const timezoneOffsets = calculateTimezoneOffsets(currentDate)
        const activityDateWithTZ = addHours(
          activityDate,
          timezoneOffsets.browserOffset
        )

        return (
          isSameDay(activityDateWithTZ, currentDate) ||
          (isBefore(activityDateWithTZ, currentDate) &&
            isAfter(
              addHours(
                new Date(activity.datetimeEnd),
                timezoneOffsets.browserOffset
              ),
              currentDate
            ))
        )
      })

      dayActivities.forEach(activity => {
        const timezoneOffsets = calculateTimezoneOffsets(currentDate)
        const timeData = calculateActivityTimes(
          activity,
          currentDate,
          timezoneOffsets
        )

        const nodes = createActivityNodes(activity, timeData, dayOffset)
        const vehicleNodes = createVehicleNodes(
          activity,
          timeData,
          currentDate,
          dayOffset
        )

        result.vehicleNodes.push(vehicleNodes)
        categorizeActivityNodes(
          nodes,
          activity,
          timeData.realDifferenceMs,
          result
        )
      })
    }

    const weekDayOffsetCalculator = (
      item: Issue | SpecificConditionNode | Infringement
    ) => {
      const timestamp =
        'timeOfIssueStart' in item
          ? item.timeOfIssueStart
          : 'infringementTimestamp' in item
          ? item.infringementTimestamp
          : item?.timestamp
      const itemDate = new Date(timestamp)
      const itemLocal = addHours(
        itemDate,
        timezoneData ? getLocaleCorrectionOffset(timezoneData, itemDate) : 0
      )
      const dayIndex = differenceInCalendarDays(
        startOfDay(itemLocal),
        startOfDay(periodStart)
      )
      return dayIndex >= 0 && dayIndex <= 6 ? dayIndex * 24 : -1
    }

    const colorfulVehicleNodes = optimizeAndColorizeVehicleNodes(
      result.vehicleNodes
    )
    const issueNodes = processTimeBasedNodes(
      issues,
      'issue',
      weekDayOffsetCalculator
    )
    const specificConditionNodes = processTimeBasedNodes(
      specificConditions,
      'specificCondition',
      weekDayOffsetCalculator
    )

    Object.values(result).forEach(sortNodesByStart)
    sortNodesByStart(issueNodes)
    sortNodesByStart(specificConditionNodes)

    return {
      ...result,
      issueNodes,
      specificConditionNodes,
      countryNodes: [],
      vehicleNodes: colorfulVehicleNodes,
    }
  })()

  const monthData: RangeNodeCollection = (() => {
    if (!isMonthlyMode)
      return {
        issueNodes: [],
        infringementNodes: [],
        specificConditionNodes: [],
        workNodes: [],
        availableNodes: [],
        restNodes: [],
        pauseNodes: [],
        driveNodes: [],
        countryNodes: [],
        vehicleNodes: [],
      }

    const startDate = customDaysCount ? date : startOfMonth(date)
    const daysToProcess = customDaysCount || getDaysInMonth(date)

    const result: Omit<RangeNodeCollection, 'issueNodes' | 'countryNodes'> = {
      workNodes: [],
      infringementNodes: [],
      specificConditionNodes: [],
      restNodes: [],
      pauseNodes: [],
      availableNodes: [],
      driveNodes: [],
      vehicleNodes: [],
    }

    for (let dayIndex = 0; dayIndex < daysToProcess; dayIndex++) {
      const currentDate = addDays(startDate, dayIndex)
      const dayOffset = dayIndex * 24

      const dayActivities = activities.filter(activity => {
        const activityDate = new Date(activity.datetimeStart)
        const timezoneOffsets = calculateTimezoneOffsets(currentDate)
        const activityDateWithTZ = addHours(
          activityDate,
          timezoneOffsets.browserOffset
        )
        const activityEndWithTZ = addHours(
          new Date(activity.datetimeEnd),
          timezoneOffsets.browserOffset
        )

        return (
          isSameDay(activityDateWithTZ, currentDate) ||
          (isBefore(activityDateWithTZ, currentDate) &&
            isAfter(activityEndWithTZ, currentDate))
        )
      })

      dayActivities.forEach(activity => {
        const timezoneOffsets = calculateTimezoneOffsets(currentDate)
        const timeData = calculateActivityTimes(
          activity,
          currentDate,
          timezoneOffsets
        )

        const nodes = createActivityNodes(activity, timeData, dayOffset)
        const vehicleNodes = createVehicleNodes(
          activity,
          timeData,
          currentDate,
          dayOffset
        )

        result.vehicleNodes.push(vehicleNodes)

        categorizeActivityNodes(
          nodes,
          activity,
          timeData.realDifferenceMs,
          result
        )
      })
    }

    addWeekendNodesIfMissing(result, startDate, daysToProcess, false)

    const monthDayOffsetCalculator = (
      item: Issue | SpecificConditionNode | Infringement
    ) => {
      const timestamp =
        'timeOfIssueStart' in item
          ? item.timeOfIssueStart
          : 'infringementTimestamp' in item
          ? item.infringementTimestamp
          : item?.timestamp
      const itemDate = new Date(timestamp)
      const itemLocal = addHours(
        itemDate,
        timezoneData ? getLocaleCorrectionOffset(timezoneData, itemDate) : 0
      )
      const dayOffset =
        differenceInCalendarDays(startOfDay(itemLocal), startOfDay(startDate)) *
        24

      return dayOffset >= 0 && dayOffset < daysToProcess * 24 ? dayOffset : -1
    }

    const issueNodes = processTimeBasedNodes(
      issues,
      'issue',
      monthDayOffsetCalculator
    )

    return {
      ...result,
      issueNodes,
      specificConditionNodes: [],
      countryNodes: [],
      vehicleNodes: [],
    }
  })()

  return {
    day: dayData,
    week: weekData,
    month: monthData,
  }
}

export const usePDFGenerator = ({
  scheduleRef,
  setCurrentData,
  setIsLoading,
  setTypePrint,
  setStartHour,
  onErrorAuto,
}: UsePDFGeneratorParams) => {
  const translations = useTranslate({
    date: 'common.date',
    issues: 'common.issues',
    events: 'common.events',
    work: 'activities.work',
    rest: 'activities.rest',
    pause: 'common.pause',
    driving: 'activities.driving',
    available: 'common.availability',
    nHours: ['common.n-hours', { n: '' }],
    nMinutes: ['common.n-minutes', { n: 0 }],
    nIssues: ['common.n-issues', { n: 0 }],
    nSpecificCondition: ['common.n-specific-conditions', { n: 0 }],
    registrationNumber: 'activities.regnr',
    driver: 'activities.driver',

    start: 'common.start',
    end: 'common.end',
    location: 'common.location',
    workingHours: 'activities.working-hours',
    grade: 'activities.driving-grade',
    distance: 'common.distance',
    consumption: 'activities.consumption',

    hours: ['common.hours', val => val.toLowerCase()],
    km: 'common.km',
    liters: ['common.liters', val => val.toLowerCase()],
  })

  const waitForDOMUpdate = useCallback(
    (timeout: number = 500): Promise<void> => {
      return new Promise(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(resolve, timeout)
          })
        })
      })
    },
    []
  )

  const getWeekDates = (baseDate: Date): Date[] => {
    const week: Date[] = []
    const startOfWeek = new Date(baseDate)
    const day = startOfWeek.getDay()
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1)
    startOfWeek.setDate(diff)

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek)
      date.setDate(startOfWeek.getDate() + i)
      week.push(date)
    }

    return week
  }

  const createPDF = useCallback((): jsPDF => {
    return new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    })
  }, [])

  const addReportData = useCallback(
    (
      pdf: jsPDF,
      reportData: ReportDataItem[],
      startX: number = 10,
      colWidth: number = 45,
      maxCols: number = 6,
      lineHeight: number = 5
    ): number => {
      let currentY = 20
      pdf.setFontSize(10)

      for (let i = 0; i < reportData.length; i += maxCols) {
        const chunk = reportData.slice(i, i + maxCols)

        let labelRowHeight = 0
        chunk.forEach(([label]) => {
          const textLines = pdf.splitTextToSize(label ?? '-', colWidth - 4)
          labelRowHeight = Math.max(
            labelRowHeight,
            textLines.length * lineHeight + 4
          )
        })

        chunk.forEach(([label], index) => {
          const x = startX + index * colWidth
          const textLines = pdf.splitTextToSize(label ?? '-', colWidth - 4)
          pdf.text(textLines, x + 2, currentY + lineHeight)
        })
        currentY += labelRowHeight

        let valueRowHeight = 0
        chunk.forEach(([_, value]) => {
          const textLines = pdf.splitTextToSize(
            String(value ?? '-'),
            colWidth - 4
          )
          valueRowHeight = Math.max(
            valueRowHeight,
            textLines.length * lineHeight + 4
          )
        })

        chunk.forEach(([_, value], index) => {
          const x = startX + index * colWidth
          const textLines = pdf.splitTextToSize(
            String(value ?? '-'),
            colWidth - 4
          )
          pdf.text(textLines, x + 2, currentY + lineHeight)
        })
        currentY += valueRowHeight + 5
      }

      return currentY + 10
    },
    []
  )

  const createCanvasFromElement = useCallback(
    async (element: HTMLElement): Promise<HTMLCanvasElement> => {
      return await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        logging: false,
      })
    },
    []
  )

  const addImageToPDF = useCallback(
    (
      pdf: jsPDF,
      canvas: HTMLCanvasElement,
      currentY: number,
      isFirstPage: boolean = false,
      chartsOnPage: number = 1
    ): number => {
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()

      const leftMargin = 10
      const rightMargin = 10
      const availableWidth = pdfWidth - leftMargin - rightMargin

      let availableHeight: number

      if (isFirstPage) {
        availableHeight = (pdfHeight - currentY - 20) / chartsOnPage
      } else {
        availableHeight = (pdfHeight - 20) / chartsOnPage
      }

      const imgData = canvas.toDataURL('image/png')

      const ratioByWidth = availableWidth / canvas.width
      const ratioByHeight = availableHeight / canvas.height
      const ratio = Math.min(ratioByWidth, ratioByHeight)

      const imgWidth = canvas.width * ratio
      const imgHeight = canvas.height * ratio

      const x = leftMargin + (availableWidth - imgWidth) / 2

      const yOffset = (availableHeight - imgHeight) / 2
      const finalY = currentY + yOffset

      pdf.addImage(imgData, 'PNG', x, finalY, imgWidth, imgHeight)

      return currentY + availableHeight
    },
    []
  )

  const processMonthType = useCallback(
    async (pdf: jsPDF, currentY: number): Promise<number> => {
      const y = currentY
      await waitForDOMUpdate()
      const element = scheduleRef.current
      if (!element) return 0

      const canvas = await createCanvasFromElement(element)
      addImageToPDF(pdf, canvas, currentY, true, 1)
      return y
    },
    [createCanvasFromElement, addImageToPDF, scheduleRef, waitForDOMUpdate]
  )

  const processWeekType = useCallback(
    async (
      pdf: jsPDF,
      date: Date,
      currentY: number,
      fullWeek: boolean,
      originalType: TypeChart
    ): Promise<number> => {
      let y = currentY
      const weekDates = getWeekDates(date)
      let isFirstPage = true

      if (fullWeek) {
        setTypePrint('day')
        await waitForDOMUpdate()

        for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
          const currentDate = weekDates[dayIndex]
          if (!currentDate) continue
          setCurrentData(currentDate)
          await waitForDOMUpdate()

          const element = scheduleRef.current
          if (!element) continue

          const canvas = await createCanvasFromElement(element)

          if (isFirstPage) {
            y = addImageToPDF(pdf, canvas, y, true, 1)
            isFirstPage = false
          } else {
            if ((dayIndex - 1) % 2 === 0) {
              pdf.addPage()
              y = 10
            }

            y = addImageToPDF(pdf, canvas, y, false, 2)
          }
        }
      } else {
        setStartHour(0)
        setTypePrint('month')
        await waitForDOMUpdate()

        const element = scheduleRef.current
        if (!element) return y

        const canvas = await createCanvasFromElement(element)
        y = addImageToPDF(pdf, canvas, y, true, 1)
      }

      setTypePrint(originalType)
      return y
    },
    [
      setTypePrint,
      waitForDOMUpdate,
      setCurrentData,
      scheduleRef,
      createCanvasFromElement,
      addImageToPDF,
      setStartHour,
    ]
  )

  const processSingleView = useCallback(
    async (pdf: jsPDF, currentY: number): Promise<void> => {
      await waitForDOMUpdate()
      const element = scheduleRef.current
      if (!element) return

      const canvas = await createCanvasFromElement(element)
      addImageToPDF(pdf, canvas, currentY, true, 1)
    },
    [createCanvasFromElement, addImageToPDF, scheduleRef, waitForDOMUpdate]
  )

  const prepareReportData = useCallback(
    (
      type: TypeChart,
      report: DriverActivityDayReportQueryReportData | null,
      user: Pick<UserNode, 'id' | 'fullName'> | null,
      date: Date
    ): ReportDataItem[] => {
      let start = startOfWeek(date, { weekStartsOn: 1 })
      let end = endOfWeek(date)
      let periodValue = `${format(start, 'dd.MM.yyyy')} - ${format(
        end,
        'dd.MM.yyyy'
      )}`
      if (type === 'month') {
        start = startOfMonth(date)
        end = endOfMonth(date)
        periodValue = `${format(start, 'dd.MM.yyyy')} - ${format(
          end,
          'dd.MM.yyyy'
        )}`
      }

      return [
        ...(type === 'day'
          ? ([
              [
                translations.start,
                report?.startTime
                  ? format(new Date(report.startTime), 'dd.MM.yyyy HH:mm')
                  : '-',
              ],
              [
                translations.end,
                report?.endTime
                  ? format(new Date(report.endTime), 'dd.MM.yyyy HH:mm')
                  : '-',
              ],
            ] as ReportDataItem[])
          : ([[translations.date, periodValue]] as ReportDataItem[])),
        [
          translations.workingHours,
          enforceHourMinuteString(report?.timeWorkTotal),
        ],
        [translations.distance, `${report?.distance ?? 0} ${translations.km}`],
        [
          translations.consumption,
          `${report?.fuelUsage ?? 0} ${translations.liters}`,
        ],
        [
          translations.registrationNumber,
          Array.isArray(report?.registrationNumbers)
            ? report.registrationNumbers.join(', ')
            : report?.registrationNumbers ?? '-',
        ],
        [translations.driver, user?.fullName ?? '-'],
        [translations.grade, report?.drivingGrade?.toString() ?? '-'],
      ]
    },
    [
      translations.consumption,
      translations.date,
      translations.distance,
      translations.driver,
      translations.end,
      translations.grade,
      translations.km,
      translations.liters,
      translations.registrationNumber,
      translations.start,
      translations.workingHours,
    ]
  )

  const generatePDF = useCallback(
    async ({
      date,
      type,
      report,
      user,
      fullWeek = false,
    }: GeneratePDFParams): Promise<void> => {
      if (!scheduleRef.current) return

      setCurrentData(date)
      setIsLoading(true)
      setTypePrint(type)

      try {
        const pdf = createPDF()
        const reportData = prepareReportData(type, report, user, date)

        const currentY = addReportData(pdf, reportData)
        switch (type) {
          case 'month':
            await processMonthType(pdf, currentY)
            break

          case 'week':
            await processWeekType(pdf, date, currentY, fullWeek, type)
            break

          default:
            await processSingleView(pdf, currentY)
            break
        }

        const filename = `schedule-${type}-${
          new Date().toISOString().split('T')[0]
        }.pdf`
        pdf.save(filename)
      } catch (error) {
        console.error('Error generating PDF:', error)
        onErrorAuto()
      } finally {
        setIsLoading(false)
        setStartHour(0)
      }
    },
    [
      scheduleRef,
      setCurrentData,
      setIsLoading,
      setTypePrint,
      createPDF,
      prepareReportData,
      addReportData,
      processMonthType,
      processWeekType,
      processSingleView,
      onErrorAuto,
      setStartHour,
    ]
  )

  return { generatePDF }
}

export const useDriverQueries = (
  userId: string,
  type: TypeChart,
  tempDate: Date,
  dateString: string
) => {
  const onErrorAuto = useOnErrorAuto()

  function getDateRange(variant: TypeChart) {
    switch (variant) {
      case 'week': {
        const weekStart = startOfWeek(tempDate, { weekStartsOn: 1 })
        const weekEnd = endOfWeek(tempDate)
        return {
          startDate: formatISO(weekStart),
          endDate: formatISO(weekEnd),
          dateForQuery: formatISO(weekStart),
        }
      }
      case 'month': {
        const monthStart = startOfMonth(tempDate)
        const monthEnd = endOfMonth(tempDate)
        return {
          startDate: formatISO(monthStart),
          endDate: formatISO(monthEnd),
          dateForQuery: formatISO(monthStart),
        }
      }
      default: {
        return {
          startDate: formatISO(tempDate),
          endDate: formatISO(addDays(tempDate, 1)),
          dateForQuery: formatISO(tempDate),
        }
      }
    }
  }

  function handleTimezoneOffset(data: any, refetch: any, field: string) {
    const timezoneData = data?.[field]?.timezoneData
    if (!timezoneData?.offset) return
    // This is pretty ugly, but untill we have a CompanyTimezone solution this'll refetch
    // the correct activities if the user's browser is in a different timezone than
    // the report's timezone. Additionally this is required to correct the date of
    // of the report if the user's browser is in a timezone that's -x UTC and report's +y UTC while x > y on
    // the day to not get the previous day.
    //TODO: clean this up when CompanyTimezone is implemented
    const offset = timezoneData.offset
    if (offset !== tempDate.getTimezoneOffset() / -60) {
      const newFetchDateString =
        dateString + `T00:00:00+` + parseNumberToTimestamp(offset)
      refetch({
        user: userId,
        date: newFetchDateString,
        dateEnd: getDateRange(type).endDate,
      })
    }
  }

  const commonOptions = {
    skip: !userId,
    variables: {
      userId,
      dateStart: getDateRange(type).startDate,
      dateEnd: getDateRange(type).endDate,
    },
    onError: onErrorAuto(),
  }

  const { data, refetch: refetchActivities } = useQuery(
    GET_ALL_DRIVER_ACTIVITIES,
    {
      ...commonOptions,
      fetchPolicy: 'network-only',
      onCompleted: data =>
        handleTimezoneOffset(
          data,
          refetchActivities,
          'driverActivityPeriodChart'
        ),
    }
  )

  const { data: issuesData } = useQuery<GetIssues, ParamChart>(
    GET_ISSUES,
    commonOptions
  )

  const { data: infringementsData } = useQuery(ALL_INFRINGEMENTS_QUERY, {
    skip: !userId,
    variables: {
      users: [userId],
      infringementTimestamp_Gte: getDateRange(type).startDate,
      infringementTimestamp_Lt: getDateRange(type).endDate,
    },
    onError: onErrorAuto(),
  })

  const { data: specificConditionsData } = useQuery<
    GetSpecificConditionsData,
    ParamChart
  >(GET_SPECIFIC_CONDITIONS, commonOptions)

  const { data: driverDataAggregated, refetch: refetchAggregated } = useQuery<
    GetAllDriverActivitiesAggregated,
    ParamChart
  >(GET_DRIVER_ACTIVITIES_AGGREGATED, {
    ...commonOptions,
    fetchPolicy: 'network-only',
    onCompleted: data =>
      handleTimezoneOffset(
        data,
        refetchAggregated,
        'driverActivityPeriodChart'
      ),
  })

  const {
    loading: activityLoading,
    data: activityData,
    refetch: refetchDay,
  } = useQuery<
    DriverActivityDayReportQuery,
    DriverActivityDayReportQueryVariables
  >(DRIVER_ACTIVITY_DAY_REPORT_QUERY, {
    fetchPolicy: 'network-only',
    variables: { user: userId, date: formatISO(tempDate) },
    onError: onErrorAuto(),
    onCompleted: data =>
      handleTimezoneOffset(data, refetchDay, 'driverActivityDayReport'),
  })

  const { data: columnData } = useQuery<
    AllDynamicColumnsQuery<ActivitiesWithReceiptCoolumnNode>,
    AllDynamicColumnsQueryVariables
  >(ALL_DYNAMIC_COLUMNS_QUERY, {
    variables: {
      moduleName: Module.ACTIVITIES,
    },
    onError: onErrorAuto(),
  })

  return {
    activityLoading,
    activityData,
    driverDataAggregated,
    issuesData,
    infringementsData,
    specificConditionsData,
    data,
    columnData,
  }
}
